import moment from 'moment';
import { createContext, useContext, useEffect, useState } from 'react';

const DEFAULT_NUMBER_OF_DAYS = 5;
const DEFAULT_FORMAT_DATE = 'L';

const getMondayBasedOnDate = (date: moment.Moment): moment.Moment => date.add(1, 'day').startOf('isoWeek');

export type AvailabilityCalendarContextType = {
  selectedDate?: string;
  endDate?: string;
  startDateForCurrentWeek?: string;
  numberOfDays?: number;
  canGoToPreviousWeek?: boolean;
  canGoToNextWeek?: boolean;
  setSelectedDate?: (date: string) => void;
  setStartDateForCurrentWeek?: (startDate: string) => void;
  setPreviousWeek?: () => void;
  setNextWeek?: () => void;
  setToday?: () => void;
  getRange?: () => string[];
};

export const AvailabilityCalendarContext = createContext<AvailabilityCalendarContextType>({});

export const useAvailabilityCalendarContext = () => useContext(AvailabilityCalendarContext);

export const AvailabilityCalendarContextProvider = (props: {
  children: React.ReactNode;
  value: Partial<AvailabilityCalendarContextType>;
}) => {
  const { children, value } = props;
  const numberOfDays = value?.numberOfDays ?? DEFAULT_NUMBER_OF_DAYS;
  const selectedDate = value?.selectedDate || '';
  const endDate = value?.endDate || '';
  const setSelectedDate = value?.setSelectedDate || (() => {});

  const [startDateForCurrentWeek, setStartDateForCurrentWeek] = useState<string>(value?.startDateForCurrentWeek || '');
  const [canGoToPreviousWeek, setCanGoToPreviousWeek] = useState<boolean>(true);
  const [canGoToNextWeek, setCanGoToNextWeek] = useState<boolean>(true);

  const updateStartDateForCurrentWeek = (modifier: (date: moment.Moment) => moment.Moment) => {
    setStartDateForCurrentWeek((prev) => (
      getMondayBasedOnDate(modifier(moment(prev))).format(DEFAULT_FORMAT_DATE)
    ));
  };

  const setToday = () => {
    setStartDateForCurrentWeek(
      getMondayBasedOnDate(moment()).format(DEFAULT_FORMAT_DATE),
    );
  };

  const setPreviousWeek = () => {
    updateStartDateForCurrentWeek((date) => date.subtract(1, 'week'));
  };

  const setNextWeek = () => {
    updateStartDateForCurrentWeek((date) => date.add(1, 'week'));
  };

  const getRange = () => {
    const start = moment(startDateForCurrentWeek);
    const monday = getMondayBasedOnDate(start);
    const range = Array.from(
      { length: numberOfDays },
      (_, i) => monday.clone().add(i, 'days').format('YYYY-MM-DD'),
    );
    return range;
  };

  useEffect(() => {
    const range = getRange();
    const today = moment().format('YYYY-MM-DD');
    const endMoment = moment(endDate);
    const nextWeekStart = moment(startDateForCurrentWeek).add(1, 'week');
    setCanGoToPreviousWeek(!range.includes(today));
    setCanGoToNextWeek(!endMoment.isValid() || nextWeekStart.isBefore(endMoment));
  }, [startDateForCurrentWeek]);

  return (
    <AvailabilityCalendarContext.Provider
      value={{
        selectedDate,
        endDate,
        startDateForCurrentWeek,
        numberOfDays,
        canGoToPreviousWeek,
        canGoToNextWeek,
        setSelectedDate,
        setStartDateForCurrentWeek,
        setToday,
        setPreviousWeek,
        setNextWeek,
        getRange,
      }}
    >
      {children}
    </AvailabilityCalendarContext.Provider>
  );
};
